// utils/fetchWithSupabaseAuth.ts
import { supabase } from "./supabase";
import { logUIError } from "../services/api-misc";

const decodeJwtPayload = (token: string): { exp?: number } | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
};

const isTokenExpiredOrExpiringSoon = (
  token: string,
  thresholdSeconds = 300,
): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;

  const nowInSeconds = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = payload.exp - nowInSeconds;

  // Refresh if already expired or expiring within threshold
  return timeUntilExpiry < thresholdSeconds;
};

const getAccessToken = async (): Promise<string | null> => {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
};

const refreshAccessToken = async (): Promise<string | null> => {
  const { data, error } = await supabase.auth.refreshSession();
  if (error) {
    console.warn("fetchWithAuthRetry: refreshSession failed", error);

    // Log critical auth failures to Slack
    logUIError({
      message: "Token refresh failed",
      page_url:
        typeof window !== "undefined" ? window.location.href : undefined,
      context: {
        component: "fetchWithAuthRetry",
        error: error.message || String(error),
        error_status: error.status,
      },
    }).catch((logErr) =>
      console.warn("logUIError failed in fetchWithAuthRetry", logErr),
    );

    return null;
  }
  return data.session?.access_token ?? null;
};

export const fetchWithAuthRetry = async (
  input: RequestInfo,
  init: RequestInit = {},
) => {
  // console.log("fetchWithAuthRetry", input, init);
  let token = await getAccessToken();

  // Critical: No session available at all
  if (!token) {
    console.warn("fetchWithAuthRetry: No token available from Supabase");
    logUIError({
      message: "No auth token available",
      page_url:
        typeof window !== "undefined" ? window.location.href : undefined,
      context: {
        component: "fetchWithAuthRetry",
        url: String(input),
      },
    }).catch((logErr) =>
      console.warn("logUIError failed in fetchWithAuthRetry", logErr),
    );
  }

  // Proactively refresh if token is expired or expiring soon (within 5 minutes)
  if (token && isTokenExpiredOrExpiringSoon(token)) {
    console.log("Token expired or expiring soon, refreshing proactively...");
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken && refreshedToken !== token) {
      token = refreshedToken;
    } else if (!refreshedToken) {
      // Refresh failed - this is already logged in refreshAccessToken
      console.warn("Failed to refresh expired/expiring token");
    } else if (refreshedToken === token) {
      // Refresh returned the same token - possible refresh loop
      console.warn("Token refresh returned same token");
      logUIError({
        message: "Token refresh returned same token (possible refresh loop)",
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        context: {
          component: "fetchWithAuthRetry",
          url: String(input),
        },
      }).catch((logErr) =>
        console.warn("logUIError failed in fetchWithAuthRetry", logErr),
      );
    }
  }

  const doFetch = (authToken?: string | null) => {
    const headers: Record<string, string> = {
      ...((init.headers as Record<string, string>) || {}),
    };

    // Add Authorization header if we have a token
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    // Only add Content-Type for requests with a body (POST, PUT, PATCH)
    const method = init.method?.toUpperCase() || "GET";
    if (
      init.body &&
      (method === "POST" || method === "PUT" || method === "PATCH") &&
      !headers["Content-Type"] &&
      !headers["content-type"]
    ) {
      headers["Content-Type"] = "application/json";
    }

    return fetch(input, {
      ...init,
      headers,
    });
  };

  let response = await doFetch(token);

  if (!response.ok) {
    console.warn("Response not ok", response);
    const json = await response
      .clone()
      .json()
      .catch(() => null);
    if (json?.error_code === "TOKEN__EXPIRE_TOKEN") {
      console.warn("Token expired, refreshing...");
      const refreshedToken = await refreshAccessToken();

      if (!refreshedToken) {
        console.warn(
          "No refreshed token available; returning original response",
        );

        // Log if reactive refresh also fails (we already tried proactive refresh)
        logUIError({
          message: "Token expired and reactive refresh failed",
          page_url:
            typeof window !== "undefined" ? window.location.href : undefined,
          context: {
            component: "fetchWithAuthRetry",
            url: String(input),
            http_status: response.status,
          },
        }).catch((logErr) =>
          console.warn("logUIError failed in fetchWithAuthRetry", logErr),
        );

        return response;
      }

      response = await doFetch(refreshedToken);

      // If retry also fails with token error, log it
      if (!response.ok) {
        const retryJson = await response
          .clone()
          .json()
          .catch(() => null);
        if (retryJson?.error_code === "TOKEN__EXPIRE_TOKEN") {
          logUIError({
            message: "Token still expired after refresh (refresh loop)",
            page_url:
              typeof window !== "undefined" ? window.location.href : undefined,
            context: {
              component: "fetchWithAuthRetry",
              url: String(input),
              http_status: response.status,
            },
          }).catch((logErr) =>
            console.warn("logUIError failed in fetchWithAuthRetry", logErr),
          );
        }
      }
    }
  }

  return response;
};
